import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { connectState } from '@examdojo/angular/util';
import { CurrencyService } from '@examdojo/billing';
import { ButtonComponent } from '@examdojo/core/button';
import { emailValidator } from '@examdojo/core/form';
import { IconComponent } from '@examdojo/core/icon';
import { SingleSelectComponent } from '@examdojo/core/select';
import { TextInputComponent } from '@examdojo/ui/input';

export interface EditPayoutAccountDialogData {
  currentEmail: string;
  currentName: string;
  currentCurrency: string;
}

export interface EditPayoutAccountDialogResult {
  newEmail: string;
  newName: string;
  newCurrency: string;
}

interface PayoutAccountForm {
  email: FormControl<string>;
  name: FormControl<string>;
  currency: FormControl<string>;
}

@Component({
  selector: 'dojo-edit-payout-account-dialog',
  templateUrl: './edit-payout-account-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    IconComponent,
    SingleSelectComponent,
    TextInputComponent,
    MatDialogModule,
  ],
  styles: [
    `
      :host {
        display: block;
      }

      ::ng-deep .rounded-dialog .mat-mdc-dialog-container {
        border-radius: 1rem !important;
        overflow: hidden;
      }
    `,
  ],
})
export class EditPayoutAccountDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<EditPayoutAccountDialogComponent>);
  private readonly data = inject<EditPayoutAccountDialogData>(MAT_DIALOG_DATA);
  private readonly currencyService = inject(CurrencyService);

  readonly form = new FormGroup<PayoutAccountForm>({
    email: new FormControl<string>('', {
      validators: [Validators.required, emailValidator],
      nonNullable: true,
    }),
    name: new FormControl<string>('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
    currency: new FormControl<string>('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
  });

  readonly state = connectState({
    currencies: this.currencyService.getCurrencies(),
  });

  ngOnInit(): void {
    this.form.patchValue({
      email: this.data.currentEmail,
      name: this.data.currentName,
      currency: this.data.currentCurrency,
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  confirm(): void {
    if (!this.form.valid) {
      return;
    }

    const { email, name, currency } = this.form.getRawValue();
    this.dialogRef.close({
      newEmail: email,
      newName: name,
      newCurrency: currency,
    });
  }
}
